import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, updateDoc, arrayUnion, increment } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "./Navbar";
import AudioPlayer from "./AudioPlayer";
import vocabData from "./vocabData";

function Vocab() {
  const [selectedCategory, setSelectedCategory] = useState("Essential Greetings");
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [studyMode, setStudyMode] = useState("flashcards");
  const [showAnswer, setShowAnswer] = useState(false);
  const [quizMode, setQuizMode] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizTotal, setQuizTotal] = useState(0);
  const [selectedTip, setSelectedTip] = useState(null);
  const { currentUser } = useAuth();

  const currentWord = vocabData[selectedCategory][currentWordIndex];
  const currentQuizQuestion = quizQuestions[currentQuizIndex];

  useEffect(() => {
    if (quizMode && quizQuestions.length === 0) {
      generateQuiz();
    }
  }, [quizMode, selectedCategory]);

  const generateQuiz = () => {
    const words = vocabData[selectedCategory];
    const shuffled = [...words].sort(() => Math.random() - 0.5);
    const questions = shuffled.slice(0, 10).map((word, index) => {
      const wrongAnswers = words
        .filter(w => w.word !== word.word)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(w => w.meaning);
      
      const allAnswers = [word.meaning, ...wrongAnswers].sort(() => Math.random() - 0.5);
      
      return {
        ...word,
        answers: allAnswers,
        correctAnswer: word.meaning
      };
    });
    
    setQuizQuestions(questions);
    setQuizTotal(questions.length);
    setCurrentQuizIndex(0);
    setQuizScore(0);
    setQuizCompleted(false);
  };

  const handleQuizAnswer = async (selectedAnswer) => {
    if (quizCompleted) return;
    
    const isCorrect = selectedAnswer === currentQuizQuestion.correctAnswer;
    if (isCorrect) {
      setQuizScore(prev => prev + 1);
    }

    // Save progress for authenticated users
    if (currentUser) {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, {
          vocabularyProgress: arrayUnion({
            category: selectedCategory,
            word: currentQuizQuestion.word,
            correct: isCorrect,
            timestamp: new Date()
          }),
          quizScores: arrayUnion({
            category: selectedCategory,
            score: isCorrect ? 1 : 0,
            total: 1,
            timestamp: new Date()
          })
        });
      } catch (error) {
        console.error("Error saving vocabulary progress:", error);
      }
    }

    if (currentQuizIndex < quizTotal - 1) {
      setTimeout(() => {
        setCurrentQuizIndex(prev => prev + 1);
      }, 300);
    } else {
      setQuizCompleted(true);
    }
  };

  const nextWord = () => {
    const words = vocabData[selectedCategory];
    setCurrentWordIndex((prev) => (prev + 1) % words.length);
    setShowAnswer(false);
  };

  const prevWord = () => {
    const words = vocabData[selectedCategory];
    setCurrentWordIndex((prev) => (prev - 1 + words.length) % words.length);
    setShowAnswer(false);
  };

  const resetQuiz = () => {
    setQuizMode(false);
    setQuizQuestions([]);
    setCurrentQuizIndex(0);
    setQuizScore(0);
    setQuizCompleted(false);
    setQuizTotal(0);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Beginner": return "bg-green-100 text-green-800";
      case "Intermediate": return "bg-orange-100 text-orange-800";
      case "Advanced": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 shadow-2xl border-b-4 border-red-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <div className="text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-8">
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                  <div className="h-20 w-20 sm:h-32 sm:w-32 flex items-center justify-center relative z-10">
                    <span className="text-5xl sm:text-8xl font-black text-red-700">어</span>
                  </div>
                </div>
                <div className="sm:ml-6 mt-4 sm:mt-0">
                  <h1 className="text-3xl sm:text-5xl font-black text-gray-900 mb-2">Korean Vocabulary Builder</h1>
                  <p className="text-lg sm:text-2xl text-red-700 font-black">한국어 어휘 학습</p>
                  <p className="text-sm sm:text-lg text-gray-600 font-semibold">(Korean Vocabulary Learning)</p>
                </div>
              </div>
              
              <p className="text-xl text-gray-800 max-w-4xl mx-auto leading-relaxed font-semibold mb-8">
                Master Korean vocabulary with interactive flashcards, quizzes, and pronunciation practice
              </p>

              {/* Study Mode Buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 mb-8">
                <button
                  onClick={() => {
                    setStudyMode("flashcards");
                    setQuizMode(false);
                    setShowAnswer(false);
                  }}
                  className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center ${
                    studyMode === "flashcards" && !quizMode
                      ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-xl"
                      : "bg-white text-gray-700 hover:bg-red-50 border-2 border-red-200 hover:border-red-400"
                  }`}
                >
                  <span className="text-xl sm:text-2xl mr-2 sm:mr-3">📚</span>
                  Flashcards
                </button>
                <button
                  onClick={() => {
                    setQuizMode(true);
                    setStudyMode("quiz");
                    generateQuiz();
                  }}
                  className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center ${
                    quizMode
                      ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-xl"
                      : "bg-white text-gray-700 hover:bg-green-50 border-2 border-green-200 hover:border-green-400"
                  }`}
                >
                  <span className="text-xl sm:text-2xl mr-2 sm:mr-3">🧠</span>
                  Quiz Mode
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Category Selection */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-xl p-6 sm:p-8 border-4 border-red-200 sticky top-24">
                <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-4 sm:mb-6">Choose Category</h2>
                <div className="space-y-3">
                  {Object.keys(vocabData).map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setCurrentWordIndex(0);
                        setShowAnswer(false);
                        if (quizMode) {
                          generateQuiz();
                        }
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                        selectedCategory === category
                          ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg"
                          : "bg-white text-gray-700 hover:bg-red-50 border-2 border-red-200 hover:border-red-400"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {!quizMode ? (
                /* Flashcard Mode */
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-xl p-12 border-4 border-red-200">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-gray-900 mb-2">{selectedCategory}</h2>
                    <p className="text-lg text-gray-600 font-semibold">
                      Card {currentWordIndex + 1} of {vocabData[selectedCategory].length}
                    </p>
                  </div>

                  <div className="bg-white rounded-3xl shadow-2xl p-12 mb-8 border-4 border-red-200">
                    <div className="text-center">
                      <div className="text-8xl mb-8">{currentWord.icon || "📖"}</div>
                      <h3 className="text-6xl font-black text-red-700 mb-6">{currentWord.word}</h3>
                      <p className="text-3xl text-gray-600 font-semibold mb-8">{currentWord.roman}</p>
                      
                      {showAnswer ? (
                        <div className="space-y-6">
                          <p className="text-4xl font-bold text-gray-900">{currentWord.meaning}</p>
                          <div className="flex justify-center">
                            <AudioPlayer word={currentWord.word} roman={currentWord.roman} />
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowAnswer(true)}
                          className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-12 py-6 rounded-2xl font-bold text-xl hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          Show Answer
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-center gap-6">
                    <button
                      onClick={prevWord}
                      className="bg-white text-gray-700 px-8 py-4 rounded-2xl font-bold hover:bg-red-50 border-2 border-red-200 hover:border-red-400 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={nextWord}
                      className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              ) : (
                /* Quiz Mode */
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl shadow-xl p-12 border-4 border-green-200">
                  {!quizCompleted ? (
                    <>
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-black text-gray-900 mb-2">Vocabulary Quiz</h2>
                        <p className="text-lg text-gray-600 font-semibold">
                          Question {currentQuizIndex + 1} of {quizTotal}
                        </p>
                        <div className="w-full bg-gray-200 rounded-full h-4 mt-4">
                          <div 
                            className="h-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg transition-all duration-500"
                            style={{ width: `${((currentQuizIndex + 1) / quizTotal) * 100}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="bg-white rounded-3xl shadow-2xl p-12 mb-8 border-4 border-green-200">
                        <div className="text-center">
                          <h3 className="text-4xl font-black text-green-700 mb-8">
                            What does <span className="text-red-600">{currentQuizQuestion.word}</span> mean?
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {currentQuizQuestion.answers.map((answer, index) => (
                              <button
                                key={index}
                                onClick={() => handleQuizAnswer(answer)}
                                className="bg-gradient-to-r from-green-100 to-emerald-100 text-gray-800 px-8 py-6 rounded-2xl font-bold text-xl hover:from-green-200 hover:to-emerald-200 border-2 border-green-300 hover:border-green-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                              >
                                {answer}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Quiz Results */
                    <div className="text-center">
                      <div className="text-8xl mb-8">🎉</div>
                      <h2 className="text-4xl font-black text-gray-900 mb-6">Quiz Complete!</h2>
                      <p className="text-2xl text-gray-700 mb-8 font-semibold">
                        You scored {quizScore} out of {quizTotal} correct!
                      </p>
                      <div className="text-6xl font-black text-green-600 mb-8">
                        {Math.round((quizScore / quizTotal) * 100)}%
                      </div>
                      <div className="flex justify-center gap-6">
                        <button
                          onClick={resetQuiz}
                          className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          Try Again
                        </button>
                        <button
                          onClick={() => {
                            setQuizMode(false);
                            setStudyMode("flashcards");
                          }}
                          className="bg-white text-gray-700 px-8 py-4 rounded-2xl font-bold hover:bg-green-50 border-2 border-green-200 hover:border-green-400 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          Back to Flashcards
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Vocab;